import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { create } from "../api/API";
import { FiLock } from "react-icons/fi";
import LoadingSpinner from "../components/Generic/LoadingSpinner";
import "../styles/Payments.css";
import LoadRazorpay from "../utility/LoadRazorpay";

const Payment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [member, setMember] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Fetch payment info
  useEffect(() => {
    if (!token) {
      toast.error("Invalid payment link");
      navigate("/login");
      return;
    }

    const fetchPaymentInfo = async () => {
      try {
        const res = await create("/payments-member-info", { token });

        setMember(res.member);
        setCohort(res.cohort);

        if (res.expiresAt) {
          const expiry = new Date(res.expiresAt).getTime();
          setExpiresAt(expiry);
          setSecondsLeft(Math.max(0, Math.floor((expiry - Date.now()) / 1000)));
        }
      } catch (err) {
        console.error("Payment info error:", err);
        setExpired(true);
        toast.error("Your payment session has expired.");
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [token, navigate]);

  useEffect(() => {
    if (!expiresAt) return;

    const interval = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000),
      );

      setSecondsLeft(remaining);

      if (remaining === 0) {
        setExpired(true);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  // Retry payment (NEW)
  const handleRetryPayment = async () => {
    try {
      if (!member?.email) {
        toast.error("Unable to retry payment. Please login again.");
        navigate("/login");
        return;
      }

      setPaying(true);

      const res = await create("/retry-payment", {
        email: member.email,
      });

      if (!res?.paymentUrl) throw new Error("Payment URL missing");

      window.location.href = res.paymentUrl;
    } catch (err) {
      console.error("Retry payment error:", err);
      toast.error("Unable to retry payment. Please contact support.");
    } finally {
      setPaying(false);
    }
  };

  // Normal payment flow
  const handlePayment = async () => {
    if (paying || verifying) return;

    try {
      const loaded = await LoadRazorpay();

      if (!loaded) {
        toast.error("Unable to load payment gateway.");
        return;
      }

      setPaying(true);

      const order = await create("/payments-create-order", {
        token,
        paymentFor: "member",
        referenceId: cohort.id,
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        order_id: order.id,
        currency: "INR",
        name: "WiSE",
        description: `Cohort Registration – ${cohort.title}`,

        handler: async (response) => {
          try {
            setVerifying(true);

            await create("/payments-verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            toast.success("Payment successful!");
            navigate("/login");
          } catch (err) {
            console.error("Verify error:", err);
            toast.error("Payment verification failed.");
          } finally {
            setVerifying(false);
          }
        },

        modal: {
          ondismiss: () => {
            toast.warn("Payment not completed");
          },
        },

        prefill: {
          name: `${member.firstName} ${member.lastName}`,
          email: member.email,
        },

        theme: {
          color: "#e0b0ff",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error("Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      {!expired && expiresAt && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: secondsLeft <= 30 ? "#dc2626" : "#2563eb",
            color: "#fff",
            padding: "8px",
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          ⏳ Payment expires in {Math.floor(secondsLeft / 60)}:
          {(secondsLeft % 60).toString().padStart(2, "0")}
        </div>
      )}
      {/* FULL PAGE LOADING OVERLAY */}
      {(loading || paying || verifying) && (
        <div className="loading-overlay">
          <LoadingSpinner
            text={
              verifying
                ? "Verifying payment, please wait…"
                : paying
                  ? "Processing payment…"
                  : "Loading payment details…"
            }
          />
        </div>
      )}
      <div className="auth-page">
        {expired ? (
          /* Expired Payment UI */
          <div className="auth-box bg-white p-6 rounded-lg shadow-md max-w-md mx-auto text-center">
            <h2 className="text-xl font-bold mb-3">Payment Session Expired</h2>

            <p className="mb-4 text-gray-600">
              Your payment session has expired. You can retry safely.
            </p>

            <button
              className="auth-btn bg-primary text-white w-full"
              onClick={handleRetryPayment}
              disabled={paying}
            >
              {paying ? "Generating..." : "Retry Payment"}
            </button>
          </div>
        ) : (
          /* Normal Payment UI */
          <div className="auth-box bg-white p-0 rounded-2xl shadow-lg max-w-md mx-auto">
            <div className="checkout-container">
              {/* Header */}
              <div className="checkout-header">
                <h1>Complete your registration</h1>
                <p className="text-sub-title">
                  Confirm your details and finish payment
                </p>
              </div>
              {/* User Info */}
              <div className="checkout-info">
                <div>
                  <span>Name</span>
                  <strong>
                    {member?.firstName} {member?.lastName}
                  </strong>
                </div>
                <div>
                  <span>Email</span>
                  <strong>{member?.email}</strong>
                </div>
                <div>
                  <span>Selected cohort</span>
                  <strong>{cohort?.title}</strong>
                </div>
              </div>
              {/* Price Card */}
              <div className="checkout-price">
                <p className="text-sub-title label">Amount Payable</p>
                <h3>₹{cohort?.finalPrice}</h3>
                {cohort?.finalPrice < cohort?.price && (
                  <>
                    <p className="text-sub-title mrp">
                      Original price ₹{cohort?.price}
                    </p>
                    <p className="text-success save">
                      You save {cohort.discount?.percentage}%
                    </p>
                  </>
                )}
              </div>

              <button
                className="auth-btn bg-primary text-secondary w-full"
                onClick={handlePayment}
                disabled={paying || verifying}
              >
                {paying ? "Processing…" : "Pay & Continue"}
              </button>
              {/* Trust */}
              <p className="checkout-trust">
                <FiLock className="checkout-lock-icon" />
                Secure payment powered by Razorpay
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Payment;
